import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { KEYWORD_LANDING_SLUGS, isPseoLocationSlug } from './seo/keywordSlugs';
import { Spinner } from 'react-bootstrap';
import { Toaster } from './components/ui/sonner';
import { AuthProvider } from './context/authContext';
import { DashboardProvider } from './context/DashboardContext';
import PrivateRoute from './components/privateRoutes';
import ErrorBoundary from './components/ErrorBoundary';
import { HelmetProvider } from 'react-helmet-async'; // Manejo asincrónico de etiquetas meta para SEO
import GoogleTagRouteTracker from './components/GoogleTagRouteTracker';
// TASK-157: el detector de actualizaciones es liviano; el banner con framer-motion se carga solo si hay versión nueva
import PWAUpdatePrompt from './components/pwa/PWAUpdatePrompt';
// TASK-131 F2: Home se importa de forma estática para evitar la cascada main.js -> chunk de Home en el LCP de '/'
import Home from './pages/home';

// ✅ OPTIMIZACIÓN: Carga Diferida de Páginas (Lazy Loading) para aligerar el bundle inicial de entrada
const OnboardingWizard = lazy(() => import('./pages/company/OnboardingWizard')); // Carga el wizard de onboarding solo cuando el usuario ingresa


// TASK-160: /dashboard elige entre la vista simple ("Hoy") y el dashboard completo; todo diferido
const simpleRoute = (name) => lazy(() => import('./components/simple/SimpleRoutes').then((m) => ({ default: m[name] })));
const DashboardHome = simpleRoute('DashboardHome');
const SimpleAgendaRoute = simpleRoute('SimpleAgendaRoute');
const SimpleMoreRoute = simpleRoute('SimpleMoreRoute');

const Turns = lazy(() => import('./pages/users/turns'));
const ReserveTurn = lazy(() => import('./pages/users/reserveTurns'));
const CompanyConfig = lazy(() => import('./pages/company/config'));
const Employee = lazy(() => import('./pages/company/employee'));
const Statistics = lazy(() => import('./pages/company/statistics'));
const DashboardSummary = lazy(() => import('./pages/company/dashboardSummary'));

const DashboardLayout = lazy(() => import('./components/DashboardLayout'));

// New Refactored Pages
const SubscriptionPage = lazy(() => import('./pages/company/SubscriptionPage.jsx'));
const ServicesPage = lazy(() => import('./pages/company/ServicesPage.jsx'));
const BranchesPage = lazy(() => import('./pages/company/BranchesPage.jsx'));
const SchedulePage = lazy(() => import('./pages/company/SchedulePage.jsx'));
const ClientsPage = lazy(() => import('./pages/company/ClientsPage.jsx'));
const ReviewsPage = lazy(() => import('./pages/company/ReviewsPage.jsx')); // ✅ Módulo de Reseñas y Moderación

// Landings dinámicas por nicho (un solo componente reutilizable para todos los rubros)
const NicheLanding = lazy(() => import('./pages/landings/NicheLanding'));

// Programmatic SEO (pSEO) — Landings por Rubro y Ciudad + Hub Directorio
const PseoLocationLanding = lazy(() => import('./pages/landings/PseoLocationLanding'));
const ExploreHub = lazy(() => import('./pages/landings/ExploreHub'));
const KeywordLanding = lazy(() => import('./pages/landings/KeywordLanding')); // TASK-153: landings por intención de búsqueda

// Fase 1 SEO: Páginas de alta conversión e intención de búsqueda comercial
const PricingPage = lazy(() => import('./pages/landings/PricingPage')); // /precios — captura intent comercial
const FeaturesPage = lazy(() => import('./pages/landings/FeaturesPage')); // /funcionalidades — captura intent informacional
const AlternativePage = lazy(() => import('./pages/landings/AlternativePage')); // /alternativas/* — captura usuarios de competidores

// TASK-153: React Router v6 no admite segmentos parciales como "/turnos-:slug": esas URLs caían en
// "/:slug" (página de un negocio) y mostraban 404. Las landings pSEO se resuelven acá; un negocio
// cuyo slug empiece con "turnos-" sigue viendo su página si no coincide con un rubro de pSEO.
const SlugRoute = () => {
  const { slug } = useParams();
  return isPseoLocationSlug(slug) ? <PseoLocationLanding /> : <ReserveTurn />;
};

const PaymentSuccess = lazy(() => import('./pages/paymentSuccess'));
const BookingSuccessPage = lazy(() => import('./pages/users/BookingSuccessPage'));
const Terms = lazy(() => import('./pages/legal/Terms'));
const Privacy = lazy(() => import('./pages/legal/Privacy'));
const Support = lazy(() => import('./pages/legal/Support'));
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'));
const VerifyEmail = lazy(() => import('./pages/auth/VerifyEmail'));
const ResetPassword = lazy(() => import('./pages/auth/ResetPassword'));
const AcceptInvitation = lazy(() => import('./pages/auth/AcceptInvitation')); // TASK-159: invitaciones del equipo
const NotFound = lazy(() => import('./pages/NotFound'));
const HelpCenter = lazy(() => import('./pages/help/HelpCenter'));
const ConfirmationPage = lazy(() => import('./pages/users/ConfirmationPage'));
const CancellationPage = lazy(() => import('./pages/users/CancellationPage'));
const CancelTurn = lazy(() => import('./pages/users/CancelTurn'));
const ReschedulePage = lazy(() => import('./pages/users/ReschedulePage'));
// 🩺 Verificación Pública de Recetas Médicas Digitales (Ley 27.553)
const VerifyPrescriptionPage = lazy(() => import('./pages/public/VerifyPrescriptionPage'));

// Componente de Carga de Respaldo (Fallback) - Más sutil y sin fondo blanco brusco
const PageLoader = () => (
  <div className="d-flex justify-content-center align-items-center" style={{
    height: '100vh',
    width: '100%',
    backgroundColor: '#f8fafc', // Coincide con el fondo de la mayoría de tus páginas
    position: 'fixed',
    top: 0,
    left: 0,
    zIndex: 9999
  }}>
    <div className="text-center">
      <Spinner
        animation="border"
        variant="primary"
        role="status"
        style={{ width: '3rem', height: '3rem', borderWidth: '0.25em' }}
      >
        <span className="visually-hidden">Cargando...</span>
      </Spinner>
      <div className="mt-3 text-primary fw-medium opacity-75">Cargando uTurno...</div>
    </div>
  </div>
);

// Redirección inteligente que preserva query params como ?tab=notifications al acceder por /setting
const SettingRedirect = () => {
  const location = useLocation();
  return <Navigate to={`/settings${location.search}`} replace />;
};

function App() {
  return (
    <HelmetProvider>
      {/* TASK-131 F1: reCAPTCHA se monta solo en registro, soporte y reserva (RecaptchaBoundary) */}
        <AuthProvider>
          <Router>
            <GoogleTagRouteTracker />
            <Toaster position="top-center" richColors closeButton />
            <PWAUpdatePrompt />
            <ErrorBoundary>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<Home />} />

                  {/* SEO B2B Landing Pages (migradas al sistema dinámico con code splitting diferido) */}
                  <Route path="/software-para-barberias" element={<NicheLanding nicheKey="barber" />} />
                  <Route path="/software-para-estetica" element={<NicheLanding nicheKey="aesthetics" />} />
                  <Route path="/software-para-salud" element={<NicheLanding nicheKey="health" />} />

                  {/* Landings dinámicas por nicho */}
                  <Route path="/canchas" element={<NicheLanding nicheKey="canchas" />} />
                  <Route path="/veterinaria" element={<NicheLanding nicheKey="veterinaria" />} />
                  <Route path="/taller-mecanico" element={<NicheLanding nicheKey="taller" />} />

                  {/* Nuevos rubros: Consultoría, Academias, Inmobiliarias, Eventos y Gaming */}
                  <Route path="/consultoria" element={<NicheLanding nicheKey="consultoria" />} />
                  <Route path="/academias" element={<NicheLanding nicheKey="academias" />} />
                  <Route path="/inmobiliaria" element={<NicheLanding nicheKey="inmobiliaria" />} />
                  <Route path="/eventos" element={<NicheLanding nicheKey="eventos" />} />
                  <Route path="/gaming" element={<NicheLanding nicheKey="gaming" />} />

                  {/* FASE 1 SEO: Páginas de alta intención comercial e informacional */}
                  <Route path="/precios" element={<PricingPage />} />
                  <Route path="/funcionalidades" element={<FeaturesPage />} />

                  {/* FASE 1 SEO: Páginas de alternativas a competidores (competitor SEO) */}
                  <Route path="/alternativas/calendly" element={<AlternativePage altKey="calendly" />} />
                  <Route path="/alternativas/fresha" element={<AlternativePage altKey="fresha" />} />
                  <Route path="/alternativas/simplybook" element={<AlternativePage altKey="simplybook" />} />
                  <Route path="/alternativas/booksy" element={<AlternativePage altKey="booksy" />} />
                  <Route path="/alternativas/agendapro" element={<AlternativePage altKey="agendapro" />} />

                  {/* TASK-153: landings por intención de búsqueda (CRM, turnero, profesiones) */}
                  {KEYWORD_LANDING_SLUGS.map((slug) => (
                    <Route key={slug} path={`/${slug}`} element={<KeywordLanding slug={slug} />} />
                  ))}

                  {/* 🌐 PROGRAMMATIC SEO (pSEO): Landings geolocalizadas por rubro y localidad.
                      /turnos-<rubro>-<ciudad> se resuelve en SlugRoute (React Router v6 no admite "/turnos-:slug") */}
                  <Route path="/explorar/:rubro/:city" element={<PseoLocationLanding />} />
                  <Route path="/explorar" element={<ExploreHub />} />

                  <Route path="/confirmar-asistencia/:turnId" element={<ConfirmationPage />} />
                  <Route path="/cancelar-turno/:turnId" element={<CancellationPage />} />

                  {/* TASK-149: conservan la intención (abren el formulario correspondiente) */}
                  <Route path="/register" element={<Navigate to="/?register=true" replace />} />

                  <Route path="/login" element={<Navigate to="/?login=true" replace />} />

                  {/* Administrative Routes with Sidebar */}
                  <Route element={<PrivateRoute><DashboardProvider><DashboardLayout /></DashboardProvider></PrivateRoute>}>
                    <Route path="/summary" element={<DashboardSummary />} />
                    <Route path="/dashboard" element={<DashboardHome />} />
                    <Route path="/agenda" element={<SimpleAgendaRoute />} />
                    <Route path="/mas" element={<SimpleMoreRoute />} />
                    {/* Ruta del prototipo retirada: montaba el calendario sin props y rompía al navegar fechas. Se redirige al dashboard real. */}
                    <Route path="/dashboard-v2" element={<Navigate to="/dashboard" replace />} />
                    <Route path="/settings" element={<CompanyConfig />} />
                    {/* Alias amigable: Redirige /setting a /settings preservando parámetros de consulta (?tab=...) */}
                    <Route path="/setting" element={<SettingRedirect />} />
                    <Route path="/staff" element={<Employee />} />
                    <Route path="/analytics" element={<Statistics />} />
                    <Route path="/help" element={<HelpCenter />} />
                    <Route path="/subscription" element={<SubscriptionPage />} />
                    <Route path="/services" element={<ServicesPage />} />
                    <Route path="/branches" element={<BranchesPage />} />
                    <Route path="/schedule" element={<SchedulePage />} />

                    <Route path="/clients" element={<ClientsPage />} />
                    <Route path="/reviews" element={<ReviewsPage />} />
                    <Route path="/support" element={<Support />} />
                  </Route>

                  <Route
                    path="/onboarding"
                    element={
                      <PrivateRoute>
                        <OnboardingWizard />
                      </PrivateRoute>
                    }
                  />

                  <Route path="/terms" element={<Terms />} />
                  <Route path="/privacy" element={<Privacy />} />

                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/verify-email" element={<VerifyEmail />} />
                  <Route path="/receta/:code" element={<VerifyPrescriptionPage />} />
                  <Route path="/verificar-receta/:code" element={<VerifyPrescriptionPage />} />

                  <Route path="/reset-password/:token" element={<ResetPassword />} />
                  <Route path="/invitacion" element={<AcceptInvitation />} />

                  <Route path="/404" element={<NotFound />} />
                  <Route path="/reserva" element={<Turns />} />
                  <Route path="/cancelar-turno" element={<CancelTurn />} />
                  <Route path="/reprogramar" element={<ReschedulePage />} />
                  <Route path="/reserva/:companyId" element={<ReserveTurn />} />
                  <Route path="/:slug" element={<SlugRoute />} />
                  <Route path="/reservar/:companyId" element={<ReserveTurn />} />
                  <Route path="/payment/success" element={<PaymentSuccess />} />
                  <Route path="/booking/success" element={<BookingSuccessPage />} />
                  <Route path="/booking/failure" element={<BookingSuccessPage />} /> {/* Reuse for now, logic handles status */}
                  <Route path="/booking/pending" element={<BookingSuccessPage />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </ErrorBoundary>
          </Router>
        </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
